/**
 * Zákazníci → Vyhledávání helpers — pure, unit-tested.
 */

import type {
  CustomerSearchContractKind,
  CustomerSearchContractType,
  CustomerSearchSmer,
} from './api';

export const DEFAULT_MESICE = 36;
export const DEFAULT_SMER: CustomerSearchSmer = 'starsi';

/** Direction of the age filter around the boundary (today − N months). */
export const SMER_OPTIONS: Array<{ value: CustomerSearchSmer; label: string }> = [
  { value: 'starsi', label: 'Starší než' },
  { value: 'novejsi', label: 'Za posledních' },
];

export function isSmer(v: unknown): v is CustomerSearchSmer {
  return SMER_OPTIONS.some((o) => o.value === v);
}
export const MIN_MESICE = 1;
export const MAX_MESICE = 600;

/** Quick picks in the toolband (months). */
export const MESICE_PRESETS = [12, 24, 36, 48, 60] as const;

export const CONTRACT_TYPE_OPTIONS: Array<{ value: CustomerSearchContractType; label: string }> = [
  { value: 'any', label: 'Všechny smlouvy' },
  { value: 'vozidla', label: 'Vozidla' },
  { value: 'ojeta', label: 'Ojetá vozidla' },
  { value: 'vykup', label: 'Výkup' },
  { value: 'operak', label: 'Operativní leasing' },
];

/** Chip caption per selected kind (the Menu trigger). */
export const CONTRACT_TYPE_LABEL: Record<CustomerSearchContractType, string> = Object.fromEntries(
  CONTRACT_TYPE_OPTIONS.map((o) => [o.value, o.label]),
) as Record<CustomerSearchContractType, string>;

export const CONTRACT_KIND_LABEL: Record<CustomerSearchContractKind, string> = {
  vozidla: 'Vozidla',
  ojeta: 'Ojetá vozidla',
  vykup: 'Výkup',
  operak: 'Operativní leasing',
};

export function isContractType(v: unknown): v is CustomerSearchContractType {
  return CONTRACT_TYPE_OPTIONS.some((o) => o.value === v);
}

/**
 * Parse the months window from free text / URL. Anything that isn't a whole
 * number inside [MIN, MAX] falls back to `null` so callers can show the
 * server default (and keep the raw text in the input untouched).
 */
export function parseMesice(raw: string | number | null | undefined): number | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  if (n < MIN_MESICE || n > MAX_MESICE) return null;
  return n;
}

/** React route for a contract row of the search result. */
export function contractHref(kind: CustomerSearchContractKind, id: number): string {
  switch (kind) {
    case 'vozidla':
      return `/vozidla/smlouvy/${id}`;
    case 'ojeta':
      return `/ojeta-vozidla/smlouvy/${id}`;
    case 'vykup':
      return `/ojeta-vozidla/vykup/${id}`;
    case 'operak':
      return `/leasing/${id}`;
  }
}

/** "3 roky" / "18 měsíců" style label for the crown. */
export function mesiceLabel(mesice: number): string {
  if (mesice % 12 === 0) {
    const years = mesice / 12;
    if (years === 1) return '1 rok';
    if (years >= 2 && years <= 4) return `${years} roky`;
    return `${years} let`;
  }
  if (mesice === 1) return '1 měsíc';
  if (mesice >= 2 && mesice <= 4) return `${mesice} měsíce`;
  return `${mesice} měsíců`;
}
